import {useMutation} from "@apollo/client";
import isEmpty from "lodash/isEmpty";

import {FormModal} from "@/components/form/overlay/FormModal.tsx";
import {UpErrorBoundary} from "@/lib/handlers/UpErrorBoundary.tsx";
import {GQL_CREDENTIAL_CREATE, GQL_CREDENTIALS_QUERY} from "@/lib/queries/credentials.ts";
import {ResponseStatus, ServerErrors} from "@/lib/types/common.ts";
import {CredentialCreateResponse, Credentials, CredentialType} from "@/lib/types/credentials.ts";
import {handleFormatingCertificateFiles} from "@/pages/vault/vaultForm/CredentialFormUtils.ts";

import {CreateCredentialFormValues, CredentialCreateForm} from "./CredentialCreateForm.tsx";

interface CredentialMutationDrawerProps {
  show: boolean;
  setShow: (show: boolean) => void;
  handleCreate?: (credential: Credentials) => void;
}

export const CredentialCreateModal = ({show, setShow, handleCreate}: CredentialMutationDrawerProps) => {
  const formId = "create-credential-form";
  const [createCredential, {data, loading, error, reset}] = useMutation<CredentialCreateResponse>(
    GQL_CREDENTIAL_CREATE,
    {
      refetchQueries: [GQL_CREDENTIALS_QUERY],
    }
  );
  let errorMessage: ServerErrors = {__all__: []};
  if (data?.response?.status === ResponseStatus.ERROR) {
    errorMessage = {...errorMessage, ...data?.response?.message};
  }
  if (error || !isEmpty(errorMessage.non_field_errors)) {
    errorMessage.__all__.push("something went wrong please contact support");
  }

  const handleSubmit = async (data: CreateCredentialFormValues) => {
    await handleFormatingCertificateFiles(data);

    const response = await createCredential({
      variables: {
        ...data,
      },
    });
    const responseData = response.data?.response;
    if (responseData?.status === ResponseStatus.SUCCESS) {
      if (handleCreate) {
        handleCreate(responseData.data);
      }
      setShow(false);
    }
  };

  const handleCancel = () => {
    reset();
    setShow(false);
  };

  return (
    <FormModal
      size={"lg"}
      centered
      show={show}
      onCancel={handleCancel}
      formId={formId}
      hasRequiredFields
      disabled={loading}
      title="Create Vault Item"
    >
      <UpErrorBoundary>
        <CredentialCreateForm
          credentialType={CredentialType.TOKEN}
          formId={formId}
          onSubmit={handleSubmit}
          serverErrors={errorMessage}
        />
      </UpErrorBoundary>
    </FormModal>
  );
};
